// components/audio/audioGenerationInfo.js
// Records and reads which voice/model produced a stop's audio, and when.
import { AVAILABLE_VOICES } from '../ItineraryCreator/utils/voiceConstants';

export const LATEST_TTS_MODEL = 'gemini-3.8-flash-tts';

const MODEL_LABELS = {
  'gemini-3.8-flash-tts': 'Gemini 3.8 Flash',
  'gemini-3.1-flash-tts-preview': 'Gemini 3.1 Flash',
  'gemini-2.5-pro-preview-tts': 'Gemini 2.5 Pro Preview',
  'gemini-2.5-pro-tts': 'Gemini 2.5 Pro',
  'gemini-2.5-flash-tts': 'Gemini 2.5 Flash',
  'gemini-2.5-flash-preview-tts': 'Gemini 2.5 Flash'
};

// Backend storage paths end with `_${Date.now()}.mp3`; older audio has no recorded date,
// so recover it from there. Only accept plausible millisecond timestamps (2020-2100).
const MIN_FILENAME_TIMESTAMP = Date.UTC(2020, 0, 1);
const MAX_FILENAME_TIMESTAMP = Date.UTC(2100, 0, 1);

export const getAudioModelLabel = (model) => MODEL_LABELS[model] || model || 'Unknown model';

export const buildAudioGenerationStamp = (voiceKey, result) => ({
  audioGeneratedAt: new Date().toISOString(),
  audioModel: result?.metadata?.modelUsed || AVAILABLE_VOICES[voiceKey]?.model || ''
});

const getTimestampFromAudioUrls = (poi) => {
  const urls = [poi.uncompressedAudioUrl, poi.originalAudioUrl, poi.audioUrl].filter(Boolean);
  for (const url of urls) {
    let decoded = String(url);
    try {
      decoded = decodeURIComponent(decoded);
    } catch (error) {
      // Keep the raw URL if it is not valid percent-encoding
    }
    const matches = decoded.match(/\d{13}/g) || [];
    for (let i = matches.length - 1; i >= 0; i -= 1) {
      const timestamp = Number(matches[i]);
      if (timestamp >= MIN_FILENAME_TIMESTAMP && timestamp < MAX_FILENAME_TIMESTAMP) {
        return new Date(timestamp);
      }
    }
  }
  return null;
};

export const getPoiAudioGenerationInfo = (poi) => {
  const hasAudio = Boolean(poi?.audioUrl || poi?.uncompressedAudioUrl || poi?.originalAudioUrl);
  if (!hasAudio) {
    return { hasAudio: false };
  }

  const voice = AVAILABLE_VOICES[poi.ttsVoice];
  const recordedDate = poi.audioGeneratedAt ? new Date(poi.audioGeneratedAt) : null;
  const hasRecordedDate = Boolean(recordedDate && !Number.isNaN(recordedDate.getTime()));
  const generatedAt = hasRecordedDate ? recordedDate : getTimestampFromAudioUrls(poi);
  const model = poi.audioModel || voice?.model || '';

  return {
    hasAudio: true,
    voiceLabel: voice
      ? voice.name.replace(/\u{1F7E2}/gu, '').replace('(Default)', '').trim()
      : (poi.ttsVoice || 'Unknown voice'),
    model,
    modelLabel: getAudioModelLabel(model),
    // Without a recorded model we only know the voice's configured model
    modelIsRecorded: Boolean(poi.audioModel),
    isLatestModel: model === LATEST_TTS_MODEL,
    generatedAt,
    dateSource: hasRecordedDate ? 'recorded' : (generatedAt ? 'filename' : null)
  };
};
